/** @shell standard @header collapseOnScroll */
import React, { useState, useCallback, useMemo, useRef, useEffect, useReducer } from 'react';
import { View, Text, StyleSheet, Alert, Platform, useWindowDimensions } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import Animated, { useSharedValue, useAnimatedScrollHandler } from 'react-native-reanimated';
import { useAuthNavigation } from '../shared/navigation/useAuthNavigation';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../components/common/SvgIcon';
import { Toast } from '../components/common/Toast';
import DateSelectionPopup, { type DateSelectionPopupMode } from './components/DateSelectionPopup';
import FreezeCalendarPopup, { type FreezeCloseOptions } from './components/FreezeCalendarPopup';
import { CalendarGrid } from './components/CalendarGrid';
import { buildCalendarGridCellsWithDoubleBoxSpans } from './components/buildCalendarGridCells';
import { CalendarHeaderPlanCard } from './components/CalendarHeaderPlanCard';
import { CalendarMenuStrip } from './components/CalendarMenuStrip';
import { CalendarTodayMacrosCard } from './components/CalendarTodayMacrosCard';
import { getMenuImagesStripItems } from '../dashboard/components/DashboardMenuImagesStrip';
import { useMenuStore, type MenuBuilder } from '../data/domains/menu';
import { useQueryClient } from '@tanstack/react-query';
import { useSubscriptionContext, getActiveMealDayProgressFromSubscription, getDeliveryDatesConsumedByDoubleBox, collectDoubleBoxAnchorDates, filterFreezeToggleableDates } from '../data/domains/subscription';
import {
  useMenuForDateQuery,
  computeMacrosFromBuilders,
  scheduleV3MenuRefreshAfterPopupCloseForDay,
} from '../data/domains/menu';
import {
  useCalendarData,
  usePrefetchNearbyCalendarMonths,
  generateMonthData,
  isSameDayContent,
  calendarApiIconBlocksInteraction,
  calendarApiIconIsStar,
  getDefaultCalendarMonthYear,
  getDateStatusForDay,
  computeCalendarCellSize,
  CALENDAR_GRID_GAP,
  CALENDAR_CELL_BORDER_RADIUS,
  type CalendarDay,
} from '../data/domains/calendar';
import { useAuthStore } from '../shared/auth/store';
import { appTheme, useThemeColors } from '../shared/theme';
import type { ThemeColors } from '../shared/theme/store';
import moment from 'moment';
import { getCalendarLegendColors, resolveCalendarDateCellVisual } from './components/calendarDateCellVisuals';
import { getCalendarLegendSwatchesSet2, resolveCalendarDateCellVisualSet2 } from './components/calendarDateCellVisualsSet2';
import { useOrganizationSettings, useNutritionUiEnabled, useCalendarVariant } from '../data/domains/config';
import { useTranslation, getLocalizedWeekdaysMin, getSubscriptionPlanTitleFromMerged, formatDisplayMonthYear, formatDisplayDayMonth, layoutLtrStyle } from '../shared/i18n';
import { useRtl } from '../shared/i18n/useRtl';
import { ensureAsciiDateString } from '../shared/utils/apiParams';
import { formatTodayForApi, parseYmdMoment } from '../shared/utils/dateUtils';
import { hexToRgba } from '../shared/theme/colorUtils';
import {
  DASH_STUDIO_INNER,
  DASHBOARD_SECTION_GAP,
  studioCardShell,
} from '../shared/theme/studio/dashboardConstants';
import { androidFlatSurfaceStyle, androidScaleFont, androidScaleSize } from '../shared/theme/sharedStyles';
import { Clickable } from '../components/common/Clickable';
import { ButtonLoader } from '../components/load-main/ButtonLoader';
import { SCROLL_VIEW_PROPS } from '../shared/utils/scrollListPerf';
import { ScreenShell } from '../shared/theme/studio/stackScreenShell';
import { ScreenHeader, useHubHeaderTopPadding } from '../components/common/ScreenHeader';
import { TAB_HEADER_EXPANDED_HEIGHT_COMPACT } from '../shared/theme/styles/headerTokens';
import { getMenuFlowBottomOverlayPaddingBottom } from '../navigation/mainTabs/androidFloatingTabBarLayout';

const today_ref = new Date();
const EMPTY_MENU_BUILDERS: MenuBuilder[] = [];
/** Optional extra defer after press feedback (0 = open as soon as the next frame settles). */
const SHEET_OPEN_DELAY_MS = 0;
const CALENDAR_SET2_HEADER_HEIGHT = 56;
const CALENDAR_SET2_GRADIENT_HEIGHT_RATIO = 0.3;
const CALENDAR_SET2_SECTION_GAP = appTheme.spacing.lg;

/** Defer work until after tap ripple / scroll momentum finishes (replaces deprecated InteractionManager). */
function deferAfterUiSettles(task: () => void) {
  requestAnimationFrame(() => {
    requestAnimationFrame(task);
  });
}

type CalendarSheetState = {
  dateVisible: boolean;
  date: string;
  dateMode: DateSelectionPopupMode;
  freezeVisible: boolean;
  freezeMode: 'freeze' | 'unfreeze';
  /** Modal still animating closed — keep calendar non-interactive. */
  sheetAnimating: boolean;
};

type CalendarSheetAction =
  | { type: 'openDate'; date: string; mode: DateSelectionPopupMode }
  | { type: 'closeDate' }
  | { type: 'openFreeze'; mode: 'freeze' | 'unfreeze' }
  | { type: 'closeFreeze' }
  | { type: 'sheetDismissed' }
  | { type: 'resetSheets' };

const initialCalendarSheet: CalendarSheetState = {
  dateVisible: false,
  date: '',
  dateMode: 'selection',
  freezeVisible: false,
  freezeMode: 'freeze',
  sheetAnimating: false,
};

function calendarSheetReducer(state: CalendarSheetState, action: CalendarSheetAction): CalendarSheetState {
  switch (action.type) {
    case 'openDate':
      return { ...state, dateVisible: true, date: action.date, dateMode: action.mode, sheetAnimating: false };
    case 'closeDate':
      return { ...state, dateVisible: false, sheetAnimating: true };
    case 'openFreeze':
      return { ...state, freezeVisible: true, freezeMode: action.mode, sheetAnimating: false };
    case 'closeFreeze':
      return { ...state, freezeVisible: false, sheetAnimating: true };
    case 'sheetDismissed':
      return { ...state, sheetAnimating: false };
    case 'resetSheets':
      return initialCalendarSheet;
    default:
      return state;
  }
}

const CalendarActionButtons = React.memo(function CalendarActionButtons({
  is_refreshing,
  on_refresh,
  on_unfreeze,
  on_freeze,
  freeze_label,
  unfreeze_label,
  refresh_label,
  styles: s,
  colors,
}: {
  is_refreshing: boolean;
  on_refresh: () => void;
  on_unfreeze: () => void;
  on_freeze: () => void;
  freeze_label: string;
  unfreeze_label: string;
  refresh_label: string;
  styles: ReturnType<typeof createCalendarStyles>;
  colors: ThemeColors;
}) {
  return (
    <View style={s.actionButtonsRow}>
      <Clickable onPress={on_refresh} disabled={is_refreshing} accessibilityLabel={refresh_label} accessibilityRole="button">
        <View style={[s.actionPill, s.refreshActionPill]}>
          {is_refreshing ? <ButtonLoader size={12} color={colors.success} /> : <SvgIcon name="refresh" size={14} color={colors.success} />}
        </View>
      </Clickable>
      <Clickable onPress={on_unfreeze} accessibilityLabel={unfreeze_label} accessibilityRole="button">
        <View style={[s.actionPill, s.unfreezeButton]}>
          <SvgIcon name="snow" size={14} color={colors.success} />
          <Text style={[s.actionButtonText, { color: colors.success }]}>{unfreeze_label}</Text>
        </View>
      </Clickable>
      <Clickable onPress={on_freeze} accessibilityLabel={freeze_label} accessibilityRole="button">
        <View style={[s.actionPill, s.freezeButton]}>
          <SvgIcon name="freeze" size={14} color={colors.error} />
          <Text style={[s.actionButtonText, { color: colors.error }]}>{freeze_label}</Text>
        </View>
      </Clickable>
    </View>
  );
});

const NoSubscriptionCard = React.memo(function NoSubscriptionCard({
  on_buy,
  styles: s,
  no_sub_title,
  no_sub_text,
  buy_now,
  mutedColor,
  colors,
}: {
  on_buy: () => void;
  styles: ReturnType<typeof createCalendarStyles>;
  no_sub_title: string;
  no_sub_text: string;
  buy_now: string;
  mutedColor: string;
  colors: ThemeColors;
}) {
  return (
    <View style={[s.noSubscriptionCard, studioCardShell(colors)]}>
      <SvgIcon name="calendar" size={48} color={mutedColor} />
      <Text style={s.noSubTitle}>{no_sub_title}</Text>
      <Text style={s.noSubText}>{no_sub_text}</Text>
      <Clickable onPress={on_buy} accessibilityLabel={buy_now} accessibilityRole="button">
        <View style={s.buyButton}>
          <Text style={s.buyButtonText}>{buy_now}</Text>
        </View>
      </Clickable>
    </View>
  );
});

function createCalendarStyles(colors: ThemeColors) {
  const sf = androidScaleFont;
  const ss = androidScaleSize;
  const is_android = Platform.OS === 'android';
  const pill_pad_v = ss(7);
  const pill_pad_h = ss(12);
  const pill_gap = ss(6);
  const pill_icon = ss(14);
  const section_rhythm = DASHBOARD_SECTION_GAP;
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    /** Scroll sits above the full-screen wash (see same pattern on v3 Account / Dashboard). */
    scrollWrap: { flex: 1, zIndex: 1 },
    scrollContent: { paddingHorizontal: DASH_STUDIO_INNER, flexGrow: 1 as const, gap: section_rhythm },
    /** v3: solid panel — no hairline / accent rail (calmer than bordered variant). */
    calendarCard: {
      marginBottom: 0,
      backgroundColor: colors.backgroundElevated,
    },
    calendarBody: {
      paddingTop: ss(16),
      paddingBottom: ss(16),
      paddingStart: ss(18),
      paddingEnd: ss(18),
      minWidth: 0,
    },
    /** Cell size / width applied inline so month chrome matches the date grid. */
    monthRow: {
      ...layoutLtrStyle,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'flex-start' as const,
      alignSelf: 'center' as const,
      gap: CALENDAR_GRID_GAP,
      marginBottom: 8,
    },
    monthNavCircle: {
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      backgroundColor: (hexToRgba(colors.text, 0.05) as string) || colors.backgroundFade,
      borderRadius: CALENDAR_CELL_BORDER_RADIUS,
      ...androidFlatSurfaceStyle(),
    },
    monthPill: {
      flex: 1,
      minWidth: 0,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      paddingHorizontal: is_android ? ss(8) : 10,
      backgroundColor: (hexToRgba(colors.text, 0.04) as string) || colors.background,
      borderRadius: CALENDAR_CELL_BORDER_RADIUS,
    },
    monthTitle: {
      fontSize: is_android ? sf(14) : sf(15),
      lineHeight: is_android ? sf(18) : sf(20),
      fontWeight: '800' as const,
      letterSpacing: -0.2,
      color: colors.text,
      textAlign: 'center' as const,
    },
    calendarGrid: { marginBottom: appTheme.spacing.sm, alignSelf: 'center' },
    legendRow: {
      width: '100%' as const,
      flexDirection: 'row' as const,
      flexWrap: 'nowrap' as const,
      alignItems: 'center' as const,
      justifyContent: 'space-between' as const,
      paddingTop: appTheme.spacing.xs,
      marginTop: 0,
      gap: ss(4),
      paddingHorizontal: ss(2),
    },
    legendItem: { flexDirection: 'row' as const, alignItems: 'center' as const, flexShrink: 1, minWidth: 0 },
    legendPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: ss(4),
      backgroundColor: 'transparent',
      paddingVertical: 0,
      paddingHorizontal: 0,
      borderRadius: 0,
    },
    legendDot: { width: ss(7), height: ss(7), borderRadius: ss(2), flexShrink: 0 },
    legendText: {
      fontSize: sf(9),
      color: colors.textSecondary,
      fontWeight: '700',
      letterSpacing: 0.1,
      flexShrink: 1,
    },
    legendItemSet2: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: 3, flexShrink: 1, minWidth: 0 },
    legendSwatchSet2: {
      width: 16,
      height: 16,
      borderRadius: 8,
      borderWidth: 1,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      flexShrink: 0,
    },
    legendTextSet2: {
      fontSize: 9,
      color: colors.textSecondary,
      fontWeight: '500' as const,
      flexShrink: 1,
    },
    legendActionsDivider: {
      width: '100%',
      height: StyleSheet.hairlineWidth,
      backgroundColor: colors.borderLight,
      marginTop: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.sm,
    },
    dateStatusSection: {
      alignItems: 'center',
      marginBottom: 20,
      paddingVertical: 12,
    },
    dateStatusTitle: {
      fontSize: sf(16),
      fontWeight: '700',
      color: colors.warning,
      marginBottom: 4,
    },
    dateStatusMessage: {
      fontSize: sf(14),
      color: colors.textSecondary,
    },
    dateStatusTitleContainer: { alignItems: 'center', justifyContent: 'center' },
    actionButtonsRow: {
      width: '100%',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: pill_gap,
    },
    actionPill: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: pill_gap,
      paddingVertical: pill_pad_v,
      paddingHorizontal: pill_pad_h,
      borderRadius: 999,
      backgroundColor: (hexToRgba(colors.text, 0.04) as string) || colors.surfaceLight,
      ...androidFlatSurfaceStyle(),
    },
    refreshActionPill: {
      borderWidth: 1,
      borderColor: (hexToRgba(colors.success, 0.2) as string) || colors.success,
      paddingHorizontal: pill_pad_h,
      minWidth: pill_pad_h * 2 + pill_icon,
    },
    unfreezeButton: {
      borderWidth: 1,
      borderColor: (hexToRgba(colors.success, 0.2) as string) || colors.success,
      backgroundColor: (hexToRgba(colors.success, 0.14) as string) || colors.successUltra,
    },
    freezeButton: {
      borderWidth: 1,
      borderColor: (hexToRgba(colors.error, 0.2) as string) || colors.error,
      backgroundColor: (hexToRgba(colors.error, 0.12) as string) || colors.errorFade,
    },
    actionButtonText: { fontSize: sf(10), fontWeight: '800', letterSpacing: 0.35 },
    noSubscriptionCard: {
      paddingVertical: appTheme.spacing.xl,
      paddingHorizontal: DASH_STUDIO_INNER,
      marginTop: 0,
      alignItems: 'center',
    },
    noSubTitle: {
      fontSize: sf(20),
      fontWeight: '700',
      color: colors.text,
      marginTop: 20,
      paddingHorizontal: 8,
    },
    noSubText: {
      fontSize: sf(15),
      color: colors.textSecondary,
      textAlign: 'center',
      marginTop: 10,
      marginBottom: 24,
      paddingHorizontal: 8,
      lineHeight: 22,
    },
    buyButton: {
      backgroundColor: colors.primary,
      paddingHorizontal: 28,
      paddingVertical: 14,
      borderRadius: appTheme.borderRadius.round,
      minWidth: 160,
      alignItems: 'center',
    },
    buyButtonText: { color: colors.onPrimary, fontSize: sf(16), fontWeight: '600' },
    studioBelowGridSection: {
      gap: DASHBOARD_SECTION_GAP,
    },
    subscriptionPlanCardWrap: {
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingVertical: appTheme.spacing.sm,
    },
  });
}

function createCalendarSet2ChromeStyles(colors: ThemeColors) {
  return StyleSheet.create({
    gradientLayer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 0,
      overflow: 'hidden',
    },
    headerBar: {
      zIndex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: CALENDAR_SET2_HEADER_HEIGHT,
      paddingBottom: appTheme.spacing.sm,
    },
    titleText: {
      fontSize: 24,
      fontWeight: '500',
      color: colors.textInverse ?? colors.onPrimary ?? colors.white,
      textAlign: 'center',
    },
    scrollContent: {
      paddingHorizontal: appTheme.spacing.md,
      flexGrow: 1,
      gap: CALENDAR_SET2_SECTION_GAP,
    },
    scrollContentTop: {
      paddingTop: appTheme.spacing.xs,
    },
    calendarCard: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      paddingVertical: 14,
      paddingHorizontal: appTheme.spacing.md,
      marginBottom: 0,
      shadowColor: colors.shadowColor,
      ...appTheme.shadows.xs,
      overflow: 'visible',
    },
    monthRow: {
      ...layoutLtrStyle,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 10,
      paddingHorizontal: 2,
    },
    monthNavCircle: {
      width: 36,
      height: 36,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.surfaceElevated,
    },
    monthTitle: {
      flex: 1,
      fontSize: 17,
      fontWeight: '600',
      color: colors.text,
      textAlign: 'center',
      marginHorizontal: appTheme.spacing.sm,
    },
    legendRow: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 4,
      paddingTop: 8,
      paddingHorizontal: 2,
      marginTop: appTheme.spacing.xs,
    },
    actionButtonsRow: {
      width: '100%',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingHorizontal: 24,
      marginBottom: 0,
    },
    dateStatusSection: {
      alignItems: 'center',
      marginTop: 0,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
  });
}

const CalendarScreen = () => {
  const navigation = useAuthNavigation();
const insets = useSafeAreaInsets();
  const { t, locale } = useTranslation();
  const themeColors = useThemeColors();
  const today = today_ref;
  const styles = useMemo(() => createCalendarStyles(themeColors ?? appTheme.colors), [themeColors]);
  const rtl = useRtl();
  const day_names_single = useMemo(() => getLocalizedWeekdaysMin(locale), [locale]);
  const { settings } = useOrganizationSettings();
  const nutrition_ui_enabled = useNutritionUiEnabled();
  const calendar_variant = useCalendarVariant();
  const is_calendar_set2 = calendar_variant === 'set2';
  const is_calendar_set3 = calendar_variant === 'set3';
  const is_calendar_studio = calendar_variant === 'set1' || is_calendar_set3;
  const off_set_days = settings?.menu_selection_before ?? 3;
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const default_month_applied_for_sub_ref = useRef<number | null>(null);
  const [sheet, dispatchSheet] = useReducer(calendarSheetReducer, initialCalendarSheet);
  const pending_menu_nav_ref = useRef<string | null>(null);
  const opening_sheet_ref = useRef(false);
  const date_open_timer_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const freeze_open_timer_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scroll_idle_timer_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppress_cell_animations_ref = useRef(false);
  const [suppress_cell_animations, set_suppress_cell_animations] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [optimisticFreezeDelta, setOptimisticFreezeDelta] = useState(0);
  const user = useAuthStore(s => s.user);
  const query_client = useQueryClient();

  const { availableDates, freezeDates, pauseDates, isDateDayOff, isDateAvailable, refetch: refreshSubscriptionData, activeSubscriptions, subscriptions, getDayConfig, dayConfigMap, config } = useSubscriptionContext();

  const today_str = formatTodayForApi();
  const currentMonthStr = useMemo(() => {
    return moment(new Date(currentYear, currentMonth, 1)).locale('en').format('YYYY-MM');
  }, [currentYear, currentMonth]);
  const { getDateState, getDateIconFromApi, refetch: refetchCalendarData, apiFrozenDates = [] } = useCalendarData(currentMonthStr);
  usePrefetchNearbyCalendarMonths(true);
  const month_key_ref = useRef<string>('');
  const previous_month_data_ref = useRef<CalendarDay[]>([]);
  const freeze_dates_merged = useMemo(() => [...new Set([...freezeDates, ...apiFrozenDates])], [freezeDates, apiFrozenDates]);
  const pause_dates_merged = useMemo(() => [...new Set(pauseDates)], [pauseDates]);
  const double_box_anchor_set = useMemo(() => {
    const from_day_config: string[] = [];
    for (const [date_key, cfg] of Object.entries(dayConfigMap ?? {})) {
      if (cfg?.isDoubleBox) {
        const ascii = ensureAsciiDateString(date_key) || date_key;
        if (ascii) {
          from_day_config.push(ascii);
        }
      }
    }
    return new Set(collectDoubleBoxAnchorDates(subscriptions, from_day_config));
  }, [subscriptions, dayConfigMap]);

  const dates_consumed_by_double_box = useMemo(() => {
    const avail = availableDates.map(d => ensureAsciiDateString(d) || d);
    const list = getDeliveryDatesConsumedByDoubleBox(
      avail,
      date => double_box_anchor_set.has(date) || Boolean(dayConfigMap[date]?.isDoubleBox),
      double_box_anchor_set,
    );
    return new Set(list);
  }, [availableDates, dayConfigMap, double_box_anchor_set]);
  const activeSub = activeSubscriptions[0];

  useEffect(() => {
    const sub_id = activeSub?.id;
    const start_date = activeSub?.start_date;
    if (typeof sub_id !== 'number' || !start_date || default_month_applied_for_sub_ref.current === sub_id) {
      return;
    }
    const default_month = getDefaultCalendarMonthYear(start_date, activeSub?.end_date);
    // No active/future window → keep the current real month (initial state).
    if (!default_month) {
      default_month_applied_for_sub_ref.current = sub_id;
      return;
    }
    setCurrentMonth(default_month.month);
    setCurrentYear(default_month.year);
    default_month_applied_for_sub_ref.current = sub_id;
  }, [activeSub?.id, activeSub?.start_date, activeSub?.end_date]);

  const calendar_progress = useMemo(() => {
    if (!activeSub) {
      return { daysLeft: 0, pausedDaysLeft: 0, progressPercentage: 0 };
    }
    const p = getActiveMealDayProgressFromSubscription(activeSub, availableDates, subscriptions, dayConfigMap, config);
    return { daysLeft: p.daysLeft, pausedDaysLeft: p.pausedDaysLeft, progressPercentage: p.progressPercentage };
  }, [activeSub, availableDates, subscriptions, dayConfigMap, config]);
  const { daysLeft, pausedDaysLeft, progressPercentage } = calendar_progress;
  const hasActiveSubscription = activeSubscriptions.length > 0 && activeSub;
  const active_plan_title = useMemo(
    () => (activeSub ? getSubscriptionPlanTitleFromMerged(activeSub, t('account.activePlan')) : ''),
    [activeSub, t],
  );

  const month_key = `${currentYear}-${currentMonth}`;
  const month_data = useMemo(() => {
    const raw = generateMonthData(currentMonth, currentYear, today_str, freeze_dates_merged, pause_dates_merged, isDateDayOff, isDateAvailable, getDateState, getDateIconFromApi);
    if (month_key_ref.current !== month_key) {
      month_key_ref.current = month_key;
      previous_month_data_ref.current = [];
    }
    const prev = previous_month_data_ref.current;
    const merged = raw.map((raw_day, i) => {
      if (prev[i] != null && isSameDayContent(prev[i], raw_day)) {
        return prev[i];
      }
      return raw_day;
    });
    return merged;
  }, [month_key, currentMonth, currentYear, today_str, freeze_dates_merged, pause_dates_merged, isDateDayOff, isDateAvailable, getDateState, getDateIconFromApi]);

  useFocusEffect(
    useCallback(() => {
      opening_sheet_ref.current = false;
      if (sheet.sheetAnimating) {
        dispatchSheet({ type: 'sheetDismissed' });
      }
    }, [sheet.sheetAnimating]),
  );

  useFocusEffect(
    useCallback(() => {
      void refetchCalendarData();
    }, [refetchCalendarData]),
  );

  useEffect(() => {
    return () => {
      if (date_open_timer_ref.current) {
        clearTimeout(date_open_timer_ref.current);
      }
      if (freeze_open_timer_ref.current) {
        clearTimeout(freeze_open_timer_ref.current);
      }
      if (scroll_idle_timer_ref.current) {
        clearTimeout(scroll_idle_timer_ref.current);
      }
    };
  }, []);

  useEffect(() => {
    const REFRESH_INTERVAL_MS = 2.5 * 60 * 1000;
    const interval_id = setInterval(() => {
      refreshSubscriptionData();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval_id);
  }, [refreshSubscriptionData]);

  const goPrevMonth = useCallback(() => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  }, [currentMonth, currentYear]);

  const goNextMonth = useCallback(() => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  }, [currentMonth, currentYear]);

  /** Direct navigation when the cell is “Select Meal” (no sheet). */
  const navigate_to_menu_direct = useCallback(
    (date: string) => {
      const d = ensureAsciiDateString(date) || date;
      deferAfterUiSettles(() => {
        setTimeout(() => {
          navigation.navigate('DailyMenuFlow', { date: d, fromCalendar: true });
        }, SHEET_OPEN_DELAY_MS);
      });
    },
    [navigation],
  );

  /** User tapped “Go to menu” in the sheet — parent closes first; navigation runs in `onDismissComplete`. */
  const handle_request_menu_from_popup = useCallback((date: string) => {
    pending_menu_nav_ref.current = ensureAsciiDateString(date) || date;
    dispatchSheet({ type: 'closeDate' });
  }, []);

  const handle_date_sheet_dismiss_complete = useCallback(() => {
    const closed_date = sheet.date ? ensureAsciiDateString(sheet.date) || sheet.date : '';
    dispatchSheet({ type: 'sheetDismissed' });
    if (closed_date && user?.id) {
      const day_cfg = getDayConfig(closed_date);
      scheduleV3MenuRefreshAfterPopupCloseForDay(query_client, user.id, closed_date, day_cfg?.isDoubleBox === true);
    }
    const pending = pending_menu_nav_ref.current;
    if (pending) {
      pending_menu_nav_ref.current = null;
      deferAfterUiSettles(() => {
        navigation.navigate('DailyMenuFlow', { date: pending, fromCalendar: true });
      });
    }
  }, [navigation, sheet.date, user?.id, query_client, getDayConfig]);

  const handle_freeze_sheet_dismiss_complete = useCallback(() => {
    dispatchSheet({ type: 'sheetDismissed' });
  }, []);

  /** Android: reset sheet state then open (avoids invisible-modal leaving dateVisible stuck). iOS: defer for press feedback. */
  const open_date_popup = useCallback(
    (full_date: string, mode: DateSelectionPopupMode) => {
      if (Platform.OS === 'android') {
        dispatchSheet({ type: 'resetSheets' });
        dispatchSheet({ type: 'openDate', date: full_date, mode });
        return;
      }
      if (sheet.dateVisible || sheet.freezeVisible || sheet.sheetAnimating || opening_sheet_ref.current) {
        return;
      }
      opening_sheet_ref.current = true;
      deferAfterUiSettles(() => {
        date_open_timer_ref.current = setTimeout(() => {
          dispatchSheet({ type: 'openDate', date: full_date, mode });
          opening_sheet_ref.current = false;
          date_open_timer_ref.current = null;
        }, SHEET_OPEN_DELAY_MS);
      });
    },
    [sheet.dateVisible, sheet.freezeVisible, sheet.sheetAnimating],
  );

  const handle_date_press = useCallback(
    (day: CalendarDay, _index: number) => {
      if (Platform.OS !== 'android' && (sheet.dateVisible || sheet.freezeVisible || sheet.sheetAnimating || opening_sheet_ref.current)) {
        return;
      }
      if (day.date === null) {
        return;
      }
      if (dates_consumed_by_double_box.has(day.fullDate)) {
        return;
      }
      const day_moment = parseYmdMoment(day.fullDate);
      const is_past = day_moment.isBefore(moment(), 'day');
      const is_off_set = day_moment.isSameOrBefore(moment().add(off_set_days, 'days'), 'day');
      const icon_blocks = calendarApiIconBlocksInteraction(day.icon);
      const has_star = calendarApiIconIsStar(day.icon);
      if (day.isDayOff || day.isFrozen || day.isPaused || icon_blocks) {
        return;
      }
      if (is_past || has_star || day.state === 'On the way') {
        open_date_popup(day.fullDate, 'rating');
        return;
      }
      if (is_off_set) {
        open_date_popup(day.fullDate, 'displaySelection');
        return;
      }
      if (day.isAvailable) {
        if (day.state === 'Booked Meal' || day.state === 'Partially Booked Meal') {
          open_date_popup(day.fullDate, 'selection');
          return;
        }
        if (day.state === 'Select Meal') {
          navigate_to_menu_direct(day.fullDate);
          return;
        }
      } else {
        open_date_popup(day.fullDate, 'rating');
      }
    },
    [navigate_to_menu_direct, off_set_days, dates_consumed_by_double_box, open_date_popup, sheet.dateVisible, sheet.freezeVisible, sheet.sheetAnimating],
  );

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) {
      return;
    }
    setIsRefreshing(true);
    try {
      await refetchCalendarData();
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refetchCalendarData]);

  const palette = themeColors ?? appTheme.colors;
  const theme_style_key = useMemo(
    () => [palette.primary, palette.surface, palette.text, palette.textSecondary, palette.background].join('|'),
    [palette.primary, palette.surface, palette.text, palette.textSecondary, palette.background],
  );
  const legend_colors = useMemo(() => getCalendarLegendColors(palette), [theme_style_key, palette]);
  const legend_swatches_set2 = useMemo(() => getCalendarLegendSwatchesSet2(palette), [theme_style_key, palette]);
  const { width: window_width, height: screen_height } = useWindowDimensions();
  const calendar_cell_size = useMemo(() => computeCalendarCellSize(window_width), [window_width]);
  const calendar_grid_width = useMemo(
    () => 7 * calendar_cell_size + 6 * CALENDAR_GRID_GAP,
    [calendar_cell_size],
  );
  const month_nav_icon_size = useMemo(
    () => Math.max(16, Math.round(calendar_cell_size * 0.42)),
    [calendar_cell_size],
  );
  const set2_chrome_styles = useMemo(() => createCalendarSet2ChromeStyles(palette), [palette]);
  const set2_gradient_height = useMemo(
    () => Math.max(insets.top + CALENDAR_SET2_HEADER_HEIGHT + 40, Math.round(screen_height * CALENDAR_SET2_GRADIENT_HEIGHT_RATIO)),
    [insets.top, screen_height],
  );
  const set2_header_curve_base = useMemo(() => Math.max(window_width * 0.18, window_width / 2 + 50), [window_width]);
  const set2_header_curve_bottom_left = set2_header_curve_base * 1.18;
  const set2_header_curve_bottom_right = set2_header_curve_base * 0.82;
  const mark_calendar_scroll_active = useCallback(() => {
    if (!suppress_cell_animations_ref.current) {
      suppress_cell_animations_ref.current = true;
      set_suppress_cell_animations(true);
    }
    if (scroll_idle_timer_ref.current) {
      clearTimeout(scroll_idle_timer_ref.current);
    }
    scroll_idle_timer_ref.current = setTimeout(() => {
      suppress_cell_animations_ref.current = false;
      set_suppress_cell_animations(false);
      scroll_idle_timer_ref.current = null;
    }, 200);
  }, []);
  const on_calendar_scroll_begin = useCallback(() => {
    mark_calendar_scroll_active();
  }, [mark_calendar_scroll_active]);
  const has_active_plan = Boolean(hasActiveSubscription);

  const today_day_config = getDayConfig(today_str);

  useEffect(() => {
    previous_month_data_ref.current = month_data;
  }, [month_data]);

  const enriched_cells = useMemo(() => {
    const resolve_visual = is_calendar_set2 ? resolveCalendarDateCellVisualSet2 : resolveCalendarDateCellVisual;
    const is_double_box_anchor = (full_date: string) =>
      Boolean(full_date && (double_box_anchor_set.has(full_date) || dayConfigMap[full_date]?.isDoubleBox));
    const base = month_data.map((day, index) => {
      const is_double_box = Boolean(day.fullDate && is_double_box_anchor(day.fullDate));
      const day_for_visual = is_double_box ? { ...day, isDoubleBox: true } : day;
      const is_today_in_view =
        day.date !== null && day.date === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
      const is_past = day.fullDate ? day.fullDate < today_str : false;
      return {
        day: day_for_visual,
        index,
        visual: resolve_visual({
          day: day_for_visual,
          theme: palette,
          is_today_in_view: !!is_today_in_view,
          is_past,
          has_active_plan,
        }),
        disabled: day.isDayOff || day.isFrozen || day.isPaused || dates_consumed_by_double_box.has(day.fullDate),
      };
    });
    // Visual merge is owned by the builder (always span same-week next day). `dates_consumed_*`
    // only disables orphan follow-ups (e.g. week wrap) — do not re-gate col_span here.
    // `.cursor/rules/calendar-double-box-span.mdc`
    return buildCalendarGridCellsWithDoubleBoxSpans(base, dates_consumed_by_double_box, is_double_box_anchor);
  }, [
    month_data,
    currentMonth,
    currentYear,
    today_str,
    palette,
    has_active_plan,
    dates_consumed_by_double_box,
    dayConfigMap,
    double_box_anchor_set,
    is_calendar_set2,
  ]);

  const month_name = useMemo(() => {
    return formatDisplayMonthYear(new Date(currentYear, currentMonth, 1));
  }, [currentMonth, currentYear, locale]);

  const refetch_calendar_and_subscription = useCallback(async () => {
    await refetchCalendarData();
  }, [refetchCalendarData]);

  const handle_date_popup_close = useCallback(
    (refresh_needed?: boolean) => {
      dispatchSheet({ type: 'closeDate' });
      if (refresh_needed) {
        void refetch_calendar_and_subscription();
      }
    },
    [refetch_calendar_and_subscription],
  );

  const handle_freeze_popup_close = useCallback(
    (refresh_needed: boolean, options?: FreezeCloseOptions) => {
      dispatchSheet({ type: 'closeFreeze' });
      if (refresh_needed && options) {
        setOptimisticFreezeDelta(prev => prev + options.freeze_delta);
        setToastMessage(options.toast_message);
        setShowToast(true);
        void refetch_calendar_and_subscription().then(() => setOptimisticFreezeDelta(0));
      }
    },
    [refetch_calendar_and_subscription],
  );

  const open_freeze_popup = useCallback(
    (mode: 'freeze' | 'unfreeze') => {
      const unlockable_frozen = filterFreezeToggleableDates(freeze_dates_merged, off_set_days);
      if (mode === 'unfreeze' && unlockable_frozen.length === 0) {
        if (Platform.OS === 'android') {
          setToastMessage(t('calendar.noFrozenDatesMessage'));
          setShowToast(true);
        } else {
          Alert.alert(t('calendar.noFrozenDates'), t('calendar.noFrozenDatesMessage'));
        }
        return;
      }
      if (Platform.OS === 'android') {
        dispatchSheet({ type: 'resetSheets' });
        dispatchSheet({ type: 'openFreeze', mode });
        return;
      }
      if (sheet.dateVisible || sheet.freezeVisible || sheet.sheetAnimating || opening_sheet_ref.current) {
        return;
      }
      opening_sheet_ref.current = true;
      deferAfterUiSettles(() => {
        freeze_open_timer_ref.current = setTimeout(() => {
          dispatchSheet({ type: 'openFreeze', mode });
          opening_sheet_ref.current = false;
          freeze_open_timer_ref.current = null;
        }, SHEET_OPEN_DELAY_MS);
      });
    },
    [freeze_dates_merged, off_set_days, t, sheet.dateVisible, sheet.freezeVisible, sheet.sheetAnimating],
  );

  const on_freeze = useCallback(() => open_freeze_popup('freeze'), [open_freeze_popup]);
  const on_unfreeze = useCallback(() => open_freeze_popup('unfreeze'), [open_freeze_popup]);

  const scroll_y = useSharedValue(0);
  const on_calendar_scroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      scroll_y.value = event.contentOffset.y;
    },
  });
  const header_expanded_height = TAB_HEADER_EXPANDED_HEIGHT_COMPACT;
  const header_collapsed_height = Math.round(TAB_HEADER_EXPANDED_HEIGHT_COMPACT * 0.75);
  /** Tight breath under title-only hub header — avoid a full section gap. */
  const content_top = useHubHeaderTopPadding(header_expanded_height, appTheme.spacing.xs);
  /** Clear floating / native tab chrome so macros aren’t clipped. */
  const scroll_padding_bottom = useMemo(
    () => getMenuFlowBottomOverlayPaddingBottom(insets.bottom) + appTheme.spacing.sm,
    [insets.bottom],
  );
  const prev_icon = 'arrow-back';
  const next_icon = 'arrow-forward';

  const { data: today_menu_data } = useMenuForDateQuery(today_str, false);
  const today_builders_from_store = useMenuStore(
    state => state.menuStateByDate[today_str]?.menuBuilders ?? EMPTY_MENU_BUILDERS,
  );
  const today_builders = today_menu_data?.menuBuilders?.length ? today_menu_data.menuBuilders : today_builders_from_store;
  const calendar_menu_strip_items = useMemo(() => getMenuImagesStripItems(today_builders), [today_builders, locale]);
  const today_date_label = useMemo(() => {
    const m = parseYmdMoment(today_str);
    return m.isValid() ? formatDisplayDayMonth(m) : today_str;
  }, [today_str, locale]);
  const today_date_status = useMemo(
    () => getDateStatusForDay(month_data.find(d => d.fullDate === today_str) ?? null, t),
    [month_data, today_str, t],
  );
  const today_macros = useMemo(() => {
    const from_menu = computeMacrosFromBuilders(today_menu_data?.menuBuilders);
    if (from_menu && (from_menu.calories > 0 || from_menu.protein > 0 || from_menu.carbs > 0 || from_menu.fats > 0)) {
      return from_menu;
    }
    return {
      calories:
        today_day_config?.max_calories ??
        (activeSub?.particularsMacros as any)?.calories ??
        (activeSub?.particularsDaily as any)?.[0]?.macronutrient?.calories ??
        (activeSub?.detail as any)?.maximum_calories ??
        0,
      protein:
        (activeSub?.particularsMacros as any)?.protein ??
        (activeSub?.particularsDaily as any)?.[0]?.macronutrient?.protein ??
        0,
      carbs:
        (activeSub?.detail as any)?.maximum_carbs ??
        (activeSub?.particularsMacros as any)?.carbs ??
        (activeSub?.particularsDaily as any)?.[0]?.macronutrient?.carbs ??
        0,
      fats:
        (activeSub?.particularsMacros as any)?.fats ??
        (activeSub?.particularsDaily as any)?.[0]?.macronutrient?.fats ??
        0,
    };
  }, [today_menu_data?.menuBuilders, today_day_config, activeSub]);

  return (
    <ScreenShell colors={palette}>
    <View style={[styles.container, { backgroundColor: 'transparent' }]}>
      {is_calendar_set2 ? (
        <View
          pointerEvents="none"
          style={[
            set2_chrome_styles.gradientLayer,
            {
              height: set2_gradient_height,
              width: window_width * 1.5,
              left: -window_width / 3,
              borderBottomLeftRadius: set2_header_curve_bottom_left,
              borderBottomRightRadius: set2_header_curve_bottom_right,
              backgroundColor: palette.primary,
            },
          ]}
        />
      ) : null}
      {is_calendar_set2 ? (
        <View style={[set2_chrome_styles.headerBar, { paddingTop: insets.top }]}>
          <Text style={set2_chrome_styles.titleText} numberOfLines={1}>
            {t('calendar.mealsCalendar')}
          </Text>
        </View>
      ) : (
        <ScreenHeader
          title={t('tabs.calendar')}
          collapseOnScroll
          scrollY={scroll_y}
          expandedHeight={header_expanded_height}
          collapsedHeight={header_collapsed_height}
        />
      )}
      <Animated.ScrollView
        {...SCROLL_VIEW_PROPS}
        style={[styles.scrollWrap, is_calendar_set2 && { zIndex: 2 }]}
        contentContainerStyle={[
          is_calendar_set2 ? set2_chrome_styles.scrollContent : styles.scrollContent,
          is_calendar_set2 ? set2_chrome_styles.scrollContentTop : { paddingTop: content_top },
          { paddingBottom: scroll_padding_bottom },
        ]}
        scrollEnabled
        bounces
        overScrollMode="auto"
        onScroll={on_calendar_scroll}
        scrollEventThrottle={16}
        onScrollBeginDrag={on_calendar_scroll_begin}
        onMomentumScrollBegin={on_calendar_scroll_begin}
        onScrollEndDrag={mark_calendar_scroll_active}
        onMomentumScrollEnd={mark_calendar_scroll_active}
        keyboardShouldPersistTaps="handled">
        <View style={is_calendar_set2 ? set2_chrome_styles.calendarCard : [styles.calendarCard, studioCardShell(palette)]}>
          <View style={is_calendar_set2 ? undefined : styles.calendarBody}>
            <View
              style={
                is_calendar_set2
                  ? set2_chrome_styles.monthRow
                  : [
                      styles.monthRow,
                      {
                        width: calendar_grid_width,
                        height: calendar_cell_size,
                      },
                    ]
              }>
              <Clickable onPress={goPrevMonth} pressAnimated={false} haptics accessibilityLabel={t('calendar.previousMonth')} accessibilityRole="button">
                <View
                  style={
                    is_calendar_set2
                      ? set2_chrome_styles.monthNavCircle
                      : [
                          styles.monthNavCircle,
                          { width: calendar_cell_size, height: calendar_cell_size },
                        ]
                  }>
                  <SvgIcon
                    name={is_calendar_set2 ? 'chevron-back' : prev_icon}
                    size={is_calendar_set2 ? 18 : month_nav_icon_size}
                    color={palette.textSecondary}
                  />
                </View>
              </Clickable>
              {is_calendar_set2 ? (
                <Text style={set2_chrome_styles.monthTitle} numberOfLines={1}>
                  {month_name}
                </Text>
              ) : (
                <View style={[styles.monthPill, { height: calendar_cell_size }]}>
                  <Text style={styles.monthTitle} numberOfLines={1}>
                    {month_name}
                  </Text>
                </View>
              )}
              <Clickable onPress={goNextMonth} pressAnimated={false} haptics accessibilityLabel={t('calendar.nextMonth')} accessibilityRole="button">
                <View
                  style={
                    is_calendar_set2
                      ? set2_chrome_styles.monthNavCircle
                      : [
                          styles.monthNavCircle,
                          { width: calendar_cell_size, height: calendar_cell_size },
                        ]
                  }>
                  <SvgIcon
                    name={is_calendar_set2 ? 'chevron-forward' : next_icon}
                    size={is_calendar_set2 ? 18 : month_nav_icon_size}
                    color={palette.textSecondary}
                  />
                </View>
              </Clickable>
            </View>

            <CalendarGrid
              day_names={day_names_single}
              cells={enriched_cells}
              on_date_press={handle_date_press}
              contentContainerStyle={styles.calendarGrid}
              cell_size={calendar_cell_size}
              theme_style_key={theme_style_key}
              suppress_cell_animations={suppress_cell_animations}
              grid_variant={is_calendar_set2 ? 'set2' : 'default'}
            />

            {is_calendar_set2 ? (
              <View style={set2_chrome_styles.legendRow} accessibilityRole="list">
                {(
                  [
                    { key: 'meal', swatch: legend_swatches_set2.mealSelected, label: t('calendar.legendMeal') },
                    { key: 'frozen', swatch: legend_swatches_set2.frozenDay, label: t('calendar.legendFrozen') },
                    { key: 'paused', swatch: legend_swatches_set2.pausedDay, label: t('calendar.legendPaused') },
                    { key: 'off', swatch: legend_swatches_set2.offDay, label: t('calendar.legendOff') },
                  ] as const
                ).map(item => (
                  <View key={item.key} style={styles.legendItemSet2} accessibilityLabel={item.label}>
                    <View
                      style={[
                        styles.legendSwatchSet2,
                        { backgroundColor: item.swatch.backgroundColor, borderColor: item.swatch.borderColor },
                      ]}>
                      <SvgIcon name={item.swatch.iconName} size={9} color={item.swatch.iconColor} />
                    </View>
                    <Text style={styles.legendTextSet2} numberOfLines={1}>
                      {item.label}
                    </Text>
                  </View>
                ))}
              </View>
            ) : (
            <View style={styles.legendRow} accessibilityRole="list">
              <View style={styles.legendItem} accessibilityLabel={t('calendar.legendMeal')}>
                <View style={[styles.legendPill, { backgroundColor: 'transparent'}]}>
                  <View style={[styles.legendDot, { backgroundColor: legend_colors.mealSelected }]} />
                  <Text style={styles.legendText} numberOfLines={1}>
                    {t('calendar.legendMeal')}
                  </Text>
                </View>
              </View>
              <View style={styles.legendItem} accessibilityLabel={t('calendar.legendFrozen')}>
                <View style={[styles.legendPill, { backgroundColor: 'transparent'}]}>
                  <View style={[styles.legendDot, { backgroundColor: legend_colors.frozenDay }]} />
                  <Text style={styles.legendText} numberOfLines={1}>
                    {t('calendar.legendFrozen')}
                  </Text>
                </View>
              </View>
              <View style={styles.legendItem} accessibilityLabel={t('calendar.legendPaused')}>
                <View style={[styles.legendPill, { backgroundColor: 'transparent'}]}>
                  <View style={[styles.legendDot, { backgroundColor: legend_colors.pausedDay }]} />
                  <Text style={styles.legendText} numberOfLines={1}>
                    {t('calendar.legendPaused')}
                  </Text>
                </View>
              </View>
              <View style={styles.legendItem} accessibilityLabel={t('calendar.legendOff')}>
                <View style={[styles.legendPill, { backgroundColor: 'transparent' }]}>
                  <View style={[styles.legendDot, { backgroundColor: legend_colors.offDay }]} />
                  <Text style={styles.legendText} numberOfLines={1}>
                    {t('calendar.legendOff')}
                  </Text>
                </View>
              </View>
            </View>
            )}

            {hasActiveSubscription && !is_calendar_set2 && (
              <>
                <View style={styles.legendActionsDivider} accessibilityElementsHidden importantForAccessibility="no" />
                <CalendarActionButtons
                  is_refreshing={isRefreshing}
                  on_refresh={handleRefresh}
                  on_unfreeze={on_unfreeze}
                  on_freeze={on_freeze}
                  freeze_label={`${t('subscriptions.freeze')} ${Math.max(0, (activeSub?.used_freeze_count ?? 0) + optimisticFreezeDelta)}/${activeSub?.detail?.allowed_freeze_days || 0}`}
                  unfreeze_label={t('calendar.unfreeze')}
                  refresh_label={t('common.refresh')}
                  styles={styles}
                  colors={palette}
                />
              </>
            )}
          </View>
        </View>

        {hasActiveSubscription && is_calendar_set2 ? (
          <View style={set2_chrome_styles.actionButtonsRow}>
            <CalendarActionButtons
              is_refreshing={isRefreshing}
              on_refresh={handleRefresh}
              on_unfreeze={on_unfreeze}
              on_freeze={on_freeze}
              freeze_label={`${t('subscriptions.freeze')} ${Math.max(0, (activeSub?.used_freeze_count ?? 0) + optimisticFreezeDelta)}/${activeSub?.detail?.allowed_freeze_days || 0}`}
              unfreeze_label={t('calendar.unfreeze')}
              refresh_label={t('common.refresh')}
              styles={styles}
              colors={palette}
            />
          </View>
        ) : null}

        {is_calendar_set2 ? (
          <View style={set2_chrome_styles.dateStatusSection}>
            {calendar_menu_strip_items.length > 0 ? (
              <CalendarMenuStrip
                items={calendar_menu_strip_items}
                title={t('dashboard.selectedMenuToday')}
                date_label={today_date_label}
              />
            ) : (
              <View style={styles.dateStatusTitleContainer}>
                <Text style={styles.dateStatusTitle}>{today_date_status.title}</Text>
                {today_date_status.message ? <Text style={styles.dateStatusMessage}>{today_date_status.message}</Text> : null}
              </View>
            )}
          </View>
        ) : hasActiveSubscription && is_calendar_set3 ? (
          <View style={styles.studioBelowGridSection}>
            <View style={[styles.subscriptionPlanCardWrap, studioCardShell(palette)]}>
              <CalendarHeaderPlanCard
                plan_name={active_plan_title}
                start_date={activeSub?.start_date ?? ''}
                end_date={activeSub?.end_date ?? ''}
                days_left={daysLeft}
                paused_days_left={pausedDaysLeft}
                progress_percentage={progressPercentage}
              />
            </View>
            <CalendarTodayMacrosCard
              calories={today_macros.calories}
              protein={today_macros.protein}
              carbs={today_macros.carbs}
              fats={today_macros.fats}
            />
          </View>
        ) : hasActiveSubscription && is_calendar_studio ? (
          nutrition_ui_enabled ? (
            <CalendarTodayMacrosCard
              calories={today_macros.calories}
              protein={today_macros.protein}
              carbs={today_macros.carbs}
              fats={today_macros.fats}
            />
          ) : null
        ) : !is_calendar_set2 ? (
          <NoSubscriptionCard
            on_buy={() => navigation.navigate('SubscriptionList')}
            styles={styles}
            no_sub_title={t('calendar.noActiveSubscription')}
            no_sub_text={t('calendar.noSubscriptionMessage')}
            buy_now={t('calendar.buyNow')}
            mutedColor={palette.textSecondary}
            colors={palette}
          />
        ) : null}
      </Animated.ScrollView>

      <DateSelectionPopup
        isVisible={sheet.dateVisible}
        date={sheet.date}
        mode={sheet.dateMode}
        isDoubleBox={Boolean(
          sheet.date &&
            (double_box_anchor_set.has(ensureAsciiDateString(sheet.date) || sheet.date) ||
              dayConfigMap[sheet.date]?.isDoubleBox),
        )}
        onClose={handle_date_popup_close}
        onNavigateToMenu={sheet.dateMode === 'selection' ? handle_request_menu_from_popup : undefined}
        onDismissComplete={handle_date_sheet_dismiss_complete}
        backdropColor={palette.backdrop}
        onClearAll={refetch_calendar_and_subscription}
        customerId={user?.id || 0}
        customerSubscriptionId={activeSub?.id}
      />

      <FreezeCalendarPopup
        visible={sheet.freezeVisible}
        onClose={handle_freeze_popup_close}
        onDismissComplete={handle_freeze_sheet_dismiss_complete}
        backdropColor={palette.backdrop}
        mode={sheet.freezeMode}
        subscription={activeSubscriptions}
        availableDates={availableDates}
        freezeDates={freeze_dates_merged}
        customerId={user?.id || 0}
      />

      <Toast message={toastMessage} visible={showToast} onHide={() => setShowToast(false)} />
    </View>
    </ScreenShell>
  );
};

export default CalendarScreen;
